/**
 * Effective CPM of a campaign, as the admin submissions page reports it.
 *
 * Clips are only paid for views gained inside the campaign's accrual window
 * (72h by default), but they keep collecting views after it closes. So the
 * money actually spent per 1,000 approved views — the effective CPM — ends up
 * below the nominal rate:
 *
 *  - `effectiveCpmCents` = earnings ÷ every approved view × 1000
 *  - `paidCpmCents`      = earnings ÷ paid (in-window) views × 1000. Below
 *    nominal only when per-clip caps or the budget cut payouts short.
 *  - `paidShare`         = paid views ÷ approved views
 *  - `efficiency`        = effective ÷ nominal CPM
 *
 * Values stay fractional (cents); every ratio is null when its denominator is 0.
 */
export interface EffectiveCpmInput {
  earningsCents: number;
  approvedViews: number;
  paidViews: number;
  nominalCpmCents: number;
}

export interface EffectiveCpmSummary {
  effectiveCpmCents: number | null;
  paidCpmCents: number | null;
  paidShare: number | null;
  efficiency: number | null;
}

const finiteOrZero = (value: number) => (Number.isFinite(value) ? value : 0);

const ratio = (numerator: number, denominator: number) => (
  denominator > 0 ? numerator / denominator : null
);

export function summarizeEffectiveCpm(input: EffectiveCpmInput): EffectiveCpmSummary {
  const earnings = finiteOrZero(input.earningsCents);
  const approved = finiteOrZero(input.approvedViews);
  const paid = finiteOrZero(input.paidViews);
  const nominal = finiteOrZero(input.nominalCpmCents);

  const effectiveCpmCents = approved > 0 ? (earnings * 1000) / approved : null;
  const paidCpmCents = paid > 0 ? (earnings * 1000) / paid : null;

  return {
    effectiveCpmCents,
    paidCpmCents,
    paidShare: ratio(paid, approved),
    efficiency: effectiveCpmCents === null ? null : ratio(effectiveCpmCents, nominal),
  };
}
